import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardSection, useAuth, Button } from '@trafiflow/shared-ui'
import { agentplatService } from '../../../services/agentplat'
import type { Agent } from '../../../services/agentplat/types'
import { MessagesSquare, Activity, User, Plus, Search, ChevronUp, ChevronDown, MessageSquare, Eye } from 'lucide-react'
import { ROUTES } from '../../../shared/config/routes'

type SortField = 'name' | 'status' | 'lastActivity' | 'role'
type SortDirection = 'asc' | 'desc'

export default function MyAgents() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const [agents, setAgents] = useState<Agent[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [sortField, setSortField] = useState<SortField>('name')
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize] = useState(25)

    useEffect(() => {
        const fetchAgents = async () => {
            try {
                setLoading(true)
                const data = await agentplatService.listAgents()
                setAgents(data)
            } catch (error: unknown) {
                const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string }
                console.error('Error fetching agents:', err)
                setError(err.message || 'Failed to load agents. Please try again later.')
            } finally {
                setLoading(false)
            }
        }

        fetchAgents()
    }, [user?.tenantId])

    // Filter and sort agents
    const filteredAndSortedAgents = useMemo(() => {
        const filtered = agents.filter(agent => {
            const roleString = agent.role ? (typeof agent.role === 'string' ? agent.role : agent.role.name) : ''
            const matchesSearch = !searchQuery || 
                agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                agent.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                roleString.toLowerCase().includes(searchQuery.toLowerCase())
            
            const matchesStatus = statusFilter === 'all' || agent.status === statusFilter
            
            return matchesSearch && matchesStatus
        })

        // Sort
        return [...filtered].sort((a, b) => {
            let aValue: string | number
            let bValue: string | number

            switch (sortField) {
                case 'name':
                    aValue = a.name.toLowerCase()
                    bValue = b.name.toLowerCase()
                    break
                case 'status':
                    aValue = a.status || 'offline'
                    bValue = b.status || 'offline'
                    break
                case 'lastActivity':
                    aValue = a.lastActivity ? new Date(a.lastActivity).getTime() : 0
                    bValue = b.lastActivity ? new Date(b.lastActivity).getTime() : 0
                    break
                case 'role':
                    aValue = a.role ? (typeof a.role === 'string' ? a.role : a.role.name) : ''
                    bValue = b.role ? (typeof b.role === 'string' ? b.role : b.role.name) : ''
                    break
                default:
                    return 0
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
            return 0
        })
    }, [agents, searchQuery, statusFilter, sortField, sortDirection])

    // Pagination
    const totalPages = Math.ceil(filteredAndSortedAgents.length / pageSize)
    const paginatedAgents = useMemo(() => {
        const start = (currentPage - 1) * pageSize
        return filteredAndSortedAgents.slice(start, start + pageSize)
    }, [filteredAndSortedAgents, currentPage, pageSize])

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortField(field)
            setSortDirection('asc')
        }
        setCurrentPage(1)
    }

    const handleStatusFilterChange = (status: string) => {
        setStatusFilter(status)
        setCurrentPage(1)
    }

    const formatLastActivity = (lastActivity?: string) => {
        if (!lastActivity) return 'Never'
        const date = new Date(lastActivity)
        const now = new Date()
        const diffMs = now.getTime() - date.getTime()
        const diffMins = Math.floor(diffMs / 60000)
        const diffHours = Math.floor(diffMs / 3600000)
        const diffDays = Math.floor(diffMs / 86400000)

        if (diffMins < 1) return 'Now'
        if (diffMins < 60) return `${diffMins}m ago`
        if (diffHours < 24) return `${diffHours}h ago`
        if (diffDays < 7) return `${diffDays}d ago`
        return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
    }

    const getStatusColor = (status?: string) => {
        switch (status) {
            case 'online':
            case 'active':
                return 'bg-tf-success'
            case 'inactive':
                return 'bg-tf-text-muted'
            case 'offline':
            default:
                return 'bg-tf-text-muted'
        }
    }

    const SortIcon = ({ field }: { field: SortField }) => {
        if (sortField !== field) return null
        return sortDirection === 'asc' ? 
            <ChevronUp className="w-4 h-4 ml-1" /> : 
            <ChevronDown className="w-4 h-4 ml-1" />
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="p-8 text-center bg-tf-feedback-error-bg rounded-lg border border-tf-feedback-error-border">
                <p className="text-tf-feedback-error-text font-medium">{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="mt-4 px-4 py-2 bg-tf-feedback-error-text text-white rounded-md hover:opacity-90 transition-colors"
                >
                    Retry
                </button>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
                <div>
                    <h1 className="text-2xl font-bold text-tf-text">My Agents</h1>
                    <p className="text-tf-text-muted">Manage and chat with your custom AI agents.</p>
                </div>
                <Button
                    variant="primary"
                    onClick={() => navigate(ROUTES.AGENTS.BUILDER)}
                    icon={<Plus className="w-4 h-4" />}
                >
                    Create Agent
                </Button>
            </div>

            {agents.length === 0 ? (
                <Card className="p-12 text-center">
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 bg-tf-bg-hover rounded-full flex items-center justify-center">
                            <MessagesSquare className="w-8 h-8 text-tf-text-muted" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-tf-text">You don't have any agents yet</h3>
                            <p className="text-tf-text-muted max-w-sm mx-auto mt-2">
                                You don't have any agents configured yet. You can purchase or create one to get started.
                            </p>
                        </div>
                        <Button
                            variant="primary"
                            onClick={() => navigate(ROUTES.AGENTS.BUILDER)}
                            icon={<Plus className="w-4 h-4" />}
                            className="mt-4"
                        >
                            Create Your First Agent
                        </Button>
                    </div>
                </Card>
            ) : (
                <>
                    {/* Search and Filters */}
                    <div className="flex flex-col gap-4 md:flex-row">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-tf-text-muted" />
                            <input
                                type="text"
                                placeholder="Search by name, description or role..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value)
                                    setCurrentPage(1)
                                }}
                                className="w-full pl-10 pr-4 py-2.5 border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>
                        <select
                            value={statusFilter}
                            onChange={(e) => handleStatusFilterChange(e.target.value)}
                            className="px-4 py-2.5 border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                            <option value="all">All statuses</option>
                            <option value="online">Online</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="offline">Offline</option>
                        </select>
                    </div>

                    {/* Results count */}
                    <div className="flex items-center justify-between text-sm text-tf-text-muted">
                        <span>
                            Showing {paginatedAgents.length} of {filteredAndSortedAgents.length} agent{filteredAndSortedAgents.length !== 1 ? 's' : ''}
                            {searchQuery && ` (filtered from ${agents.length} total)`}
                        </span>
                    </div>

                    {/* Table */}
                    <Card>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-tf-bg-muted border-b border-tf-border">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider">
                                            Agent
                                        </th>
                                        <th 
                                            className="px-4 py-3 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider cursor-pointer hover:bg-tf-bg-hover"
                                            onClick={() => handleSort('role')}
                                        >
                                            <div className="flex items-center">
                                                Role
                                                <SortIcon field="role" />
                                            </div>
                                        </th>
                                        <th 
                                            className="px-4 py-3 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider cursor-pointer hover:bg-tf-bg-hover"
                                            onClick={() => handleSort('status')}
                                        >
                                            <div className="flex items-center">
                                                Status
                                                <SortIcon field="status" />
                                            </div>
                                        </th>
                                        <th 
                                            className="px-4 py-3 text-left text-xs font-semibold text-tf-text-muted uppercase tracking-wider cursor-pointer hover:bg-tf-bg-hover"
                                            onClick={() => handleSort('lastActivity')}
                                        >
                                            <div className="flex items-center">
                                                Last Activity
                                                <SortIcon field="lastActivity" />
                                            </div>
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-tf-text-muted uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-tf-bg-card divide-y divide-tf-border">
                                    {paginatedAgents.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="px-4 py-12 text-center text-tf-text-muted">
                                                <div className="flex flex-col items-center gap-2">
                                                    <MessagesSquare className="w-8 h-8 text-tf-text-muted" />
                                                    <p>No agents found</p>
                                                    {searchQuery && (
                                                        <button
                                                            onClick={() => setSearchQuery('')}
                                                            className="text-primary hover:underline text-sm"
                                                        >
                                                            Clear search
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedAgents.map((agent) => (
                                            <tr 
                                                key={agent.id} 
                                                className="hover:bg-tf-bg-muted transition-colors cursor-pointer"
                                                onClick={() => navigate(`/agents/${agent.id}`)}
                                            >
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-3">
                                                        {agent.avatar ? (
                                                            <img
                                                                src={agent.avatar}
                                                                alt={agent.name}
                                                                className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                                                            />
                                                        ) : (
                                                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                                <User className="w-5 h-5 text-primary" />
                                                            </div>
                                                        )}
                                                        <div className="min-w-0">
                                                            <div className="font-semibold text-tf-text truncate">
                                                                {agent.name}
                                                            </div>
                                                            {agent.description && (
                                                                <div className="text-sm text-tf-text-muted truncate max-w-xs">
                                                                    {agent.description}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {agent.role ? (
                                                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-tf-feedback-info-bg text-tf-feedback-info-text">
                                                            {typeof agent.role === 'string' ? agent.role : agent.role.name}
                                                        </span>
                                                    ) : (
                                                        <span className="text-sm text-tf-text-muted">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`w-2 h-2 rounded-full ${getStatusColor(agent.status)}`}></span>
                                                        <span className="text-sm font-medium text-tf-text-sub capitalize">
                                                            {agent.status || 'Offline'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-1.5 text-sm text-tf-text-muted">
                                                        <Activity className="w-4 h-4" />
                                                        <span>{formatLastActivity(agent.lastActivity)}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            onClick={() => navigate(`/agents/${agent.id}`)}
                                                            className="p-2 text-tf-text-muted hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                                            title="View details"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => navigate(`/agents/${agent.id}?tab=chat`)}
                                                            className="p-2 text-tf-text-muted hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                                                            title="Chat"
                                                        >
                                                            <MessageSquare className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <CardSection className="border-t border-tf-border">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm text-tf-text-muted">
                                        Page {currentPage} of {totalPages}
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="secondary"
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                        >
                                            Previous
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            </CardSection>
                        )}
                    </Card>
                </>
            )}
        </div>
    )
}
